import React from 'react';
import { Col, Row } from 'antd';
import { noImageIcon } from 'ui-kit/UploadImage/icons';
import { MoreVertical } from 'react-feather';
import { formatCurrency, getStoreImageUrl } from 'utils';
import ShareLink from 'components/Dashboard/Share';
import { Button, IconButton } from 'ui-kit';
import ActiveStore from '../ActiveStore';

import '../StoreListItem.scss';
import { reverse } from 'utils/urls';
import { URLS } from 'components/Routes/urls';

const StoreListItemMoreDropdown = ({
  store,
  openEditModal,
  openDuplicateModal,
  onUpdate,
}: {
  store;
  openEditModal: (store) => void;
  openDuplicateModal: (store) => void;
  onUpdate: () => void;
}) => (
  <div className="StoreListItem_moreDropdown dropdown" id={store.id}>
    <Button
      className="store-link"
      color="textLight"
      to={reverse({
        url: URLS.STORE_DETAILS,
        params: { storeId: store.id },
      })}
    >
      View Store
    </Button>
    <Button
      className="store-link"
      color="textLight"
      onClick={(e) => {
        e.preventDefault();
        openEditModal(store);
      }}
    >
      Edit Store
    </Button>
    <Button
      className="store-link"
      color="textLight"
      onClick={(e) => {
        e.preventDefault();
        openDuplicateModal(store);
      }}
    >
      Duplicate Store
    </Button>
    <ActiveStore
      isActive={store.is_visible}
      storeId={store.id}
      storeName={store.store_name}
      onUpdate={onUpdate}
    />
  </div>
);

interface IProps {
  store;
  openDropdownId: number;
  setOpenDropdownId: (id: number) => void;
  openDuplicateModal: (store) => void;
  openEditModal: (store) => void;
  currency: string;
  onUpdate: () => void;
}

const StoreListItem = ({
  store,
  openDropdownId,
  setOpenDropdownId,
  openDuplicateModal,
  openEditModal,
  currency,
  onUpdate,
}: IProps) => {
  return (
    <div className={`StoreListItem ${store.is_visible ? 'active' : 'inactive'}`}>
      <Row className="StoreListItem_desktop" key={`${store.id}-desktop`}>
        <Col className="StoreListItem_col image" span={2}>
          <Button
            to={reverse({
              url: URLS.STORE_DETAILS,
              params: { storeId: store.id },
            })}
          >
            <img
              className="StoreListItem_image"
              src={getStoreImageUrl(store.image, noImageIcon)}
              alt="Store image unavailable"
            />
          </Button>
        </Col>
        <Col className="StoreListItem_col name" span={8}>
          <Button
            color="textDark"
            to={reverse({
              url: URLS.STORE_DETAILS,
              params: { storeId: store.id },
            })}
          >
            {store.store_name}
          </Button>
        </Col>
        <Col className="StoreListItem_col share" span={2}>
          <ShareLink color="primary" storeId={store.id} text="Share Store" />
        </Col>
        <Col className="StoreListItem_col postalCode" span={2}>
          {store.postal_code}
        </Col>
        <Col className="StoreListItem_col products" span={2}>
          {store.num_products}
        </Col>
        <Col className="StoreListItem_col orders" span={2}>
          {store.num_completed_orders}
        </Col>
        <Col className="StoreListItem_col sales" span={3}>
          {formatCurrency(store.total_payout_amount_current_month, currency)}
        </Col>
        <Col className="StoreListItem_col more" span={1}>
          <div className="StoreListItem_more">
            <IconButton
              className="StoreListItem_moreButton"
              color="textDark"
              id={store.id}
              onClick={() => {
                if (openDropdownId === store.id) {
                  setOpenDropdownId(-1);
                } else {
                  setOpenDropdownId(store.id);
                }
              }}
            >
              <MoreVertical />
            </IconButton>
            {openDropdownId === store.id ? (
              <StoreListItemMoreDropdown
                store={store}
                openEditModal={openEditModal}
                openDuplicateModal={openDuplicateModal}
                onUpdate={() => {
                  onUpdate();
                  setOpenDropdownId(-1);
                }}
              />
            ) : (
              <></>
            )}
          </div>
        </Col>
      </Row>

      <Row className="StoreListItem_mobile" key={`${store.id}-mobile`}>
        <Col className="StoreListItem_col image">
          <Button
            to={reverse({
              url: URLS.STORE_DETAILS,
              params: { storeId: store.id },
            })}
          >
            <img
              className="StoreListItem_image"
              src={store.image || noImageIcon}
              alt="Store image unavailable"
            />
          </Button>
        </Col>
        <Col className="StoreListItem_col mobile">
          <Row className="StoreListItem_detail name">
            <Button
              color="textDark"
              to={reverse({
                url: URLS.STORE_DETAILS,
                params: { storeId: store.id },
              })}
            >
              {store.store_name}
            </Button>
          </Row>
          <Row className="StoreListItem_detail postalCode">{store.postal_code}</Row>
          <Row className="StoreListItem_detail sales">
            {formatCurrency(store.total_payout_amount_current_month, currency)}
            <span className="label">sales</span>
          </Row>
          <Row className="StoreListItem_detail share">
            <ShareLink color="primary" storeId={store.id} text="Share Store" />
          </Row>
        </Col>
        <Col className="StoreListItem_col more">
          <div className="StoreListItem_more">
            <IconButton
              className="StoreListItem_moreButton"
              color="textDark"
              id={store.id}
              onClick={() => {
                if (openDropdownId === store.id) {
                  setOpenDropdownId(-1);
                } else {
                  setOpenDropdownId(store.id);
                }
              }}
            >
              <MoreVertical />
            </IconButton>
            {openDropdownId === store.id ? (
              <StoreListItemMoreDropdown
                store={store}
                openEditModal={openEditModal}
                openDuplicateModal={openDuplicateModal}
                onUpdate={onUpdate}
              />
            ) : (
              <></>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default StoreListItem;
